/*
 * A toast — a completed act, stated and then gone.
 *
 * Above every surface, because the act it reports usually closes the panel that
 * raised it: it cannot belong to that panel's stacking context.
 *
 * TOP TRAILING, and the placement is the rule rather than a preference. A
 * commit lives at the foot of whatever it commits — a sheet, a modal, a page's
 * sticky bar — so the bottom of the window is the one region a confirmation may
 * never occupy: it would cover the button that raised it, and the next click
 * would land on the toast. The top trailing corner is free on every surface, at
 * every width.
 *
 * A card that floats, so it takes the one lift and the one square edge. The
 * only color is the MARK — gold for done, rust for failed. Gold rather than a
 * green of its own: it is already the system's one accent, and a toast is
 * glanced at, so the two marks only have to be told apart from EACH OTHER. A
 * second accent hue would buy nothing the shape of the icon does not.
 */
.toasts {
  position: fixed;
  /* The window's own top trailing corner, over the chrome rather than under it:
     a toast belongs to the app, not to the page that happened to raise it, so
     it lands in the same place whichever screen you are on. */
  inset-block-start: var(--s-4);
  inset-inline-end: var(--s-4);
  z-index: var(--z-overlay);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--s-2);
  /* Only the lines themselves are reachable — the strip must not swallow clicks
     meant for the page beneath it. */
  pointer-events: none;
}

.toast {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: var(--s-2);
  max-width: min(26rem, calc(100vw - var(--s-6)));
  padding: var(--s-3) var(--s-4);
  background: var(--surface-raised);
  border: 1px solid var(--border-default);
  box-shadow: var(--shadow-raised);
  color: var(--text-primary);
  font-size: var(--text-sm);
  animation: toast-in var(--motion-fast) var(--ease-standard);
}

/* The mark carries the color; the message stays ink, so it is read as a
   sentence rather than as an alarm. */
.toast__mark {
  flex: none;
}

.toast[data-tone="success"] .toast__mark {
  color: var(--highlight);
}

.toast[data-tone="danger"] .toast__mark {
  color: var(--danger);
}

/* Taking the act back: ink, and named, so it reads as the one thing to press
   rather than as part of the sentence. */
.toast__action {
  flex: none;
  margin-inline-start: var(--s-2);
  padding: 0;
  border: 0;
  background: none;
  color: var(--text-heading);
  font: inherit;
  font-weight: var(--weight-strong);
  text-decoration: underline;
  text-underline-offset: 0.2em;
  cursor: pointer;
}

.toast__dismiss {
  flex: none;
  margin-inline-start: var(--s-2);
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
}

.toast__dismiss:hover {
  color: var(--text-primary);
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateY(calc(var(--s-2) * -1));
  }
}
